import { logUtishaDebug, logUtishaError } from 'src/app/util/log';

/**
 * Represents an audio source that can be played and mixed with other audio sources
 * for streaming via Stream's SDK
 */
export class AudioSource {
  private _audioElement: HTMLAudioElement;
  private _clonedAudioElement: HTMLAudioElement | null = null;
  private _name: string;
  // _id is now a property that can be modified from MusicPlayer
  _src: string;
  private _gain = 1.0; // Default gain is 1.0 (full volume)

  // Audio processing nodes
  private _audioContext?: AudioContext;
  private _sourceNode?: MediaElementAudioSourceNode;
  private _gainNode?: GainNode;
  private _destination?: MediaStreamAudioDestinationNode | AudioNode;

  // Stored listener references for proper cleanup
  private _onPlayHandler: (() => void) | null = null;
  private _onPauseHandler: (() => void) | null = null;
  private _onSeekedHandler: (() => void) | null = null;

  /**
   * Creates a new AudioSource
   * @param fileUrl URL to the audio file
   * @param name Display name for the audio source
   * @param gain Initial gain value (0.0 to 1.0, default 1.0)
   */
  constructor(fileUrl: string, name: string, gain = 1.0) {
    logUtishaDebug(`
===================================================================
Creating new AudioSource: ${name} (${fileUrl}) with gain ${gain}
===================================================================
`);
    this._src = fileUrl;
    this._name = name;
    this._audioElement = new Audio(fileUrl);

    // Set initial volume directly on the audio element (much lower than provided gain)
    // This helps prevent background music from being too loud
    this.gain = Math.max(0, Math.min(1, gain));

    this._audioElement.load();
    this.createClonedAudioElement();

    // Auto-trigger the onEnded callback when audio finishes playing
    this._audioElement.addEventListener('ended', () => {
      if (this._onEnded) {
        this._onEnded(this);
      }
    });

    logUtishaDebug(`AudioSource created: ${name} (${fileUrl}) with gain ${gain}`);
  }

  // Callback that will be triggered when audio finishes playing
  private _onEnded: ((source: AudioSource) => void) | null = null;

  /**
   * Sets a callback function to be called when the audio finishes playing
   * @param callback Function to call when audio ends
   */
  set onEnded(callback: (source: AudioSource) => void) {
    this._onEnded = callback;
  }

  /**
   * Cleans up resources used by this audio source
   */
  cleanup(): void {
    try {
      logUtishaDebug(`Cleaning up audio source: ${this._name}`);
      // Pause and reset both audio elements
      this._audioElement.pause();
      this._audioElement.currentTime = 0;

      if (this._clonedAudioElement) {
        this._clonedAudioElement.pause();
        this._clonedAudioElement.currentTime = 0;
      }

      // Remove stored event listeners properly
      this.removeCloneSyncListeners();

      // Disconnect any audio nodes
      this.disconnectAudioNodes();

      logUtishaDebug(`Cleaned up audio source: ${this._name}`);
    } catch (error) {
      logUtishaDebug(`Error cleaning up audio source ${this._name}: ${error}`);
    }
  }

  /**
   * Gets the audio element associated with this source
   */
  get audioElement(): HTMLAudioElement {
    return this._audioElement;
  }

  get clonedAudioElement() {
    if (!this._clonedAudioElement) {
      this.createClonedAudioElement();
    }
    if (!this._clonedAudioElement) {
      throw new Error(`Failed to create cloned audio element for ${this._name}`);
    }
    // Always ensure currentTime is in sync before returning
    if (this.playing && Math.abs(this._clonedAudioElement.currentTime - this._audioElement.currentTime) > 0.3) {
      this._clonedAudioElement.currentTime = this._audioElement.currentTime;
    }

    return this._clonedAudioElement;
  }

  private createClonedAudioElement() {
    // Remove old listeners before re-creating
    this.removeCloneSyncListeners();

    this._clonedAudioElement = new Audio(this.src);
    this._clonedAudioElement.crossOrigin = 'anonymous';
    this._clonedAudioElement.load();

    // Set up synchronization between original and cloned elements — store
    // references so removeEventListener works correctly during cleanup.
    this._onPlayHandler = () => {
      if (this._clonedAudioElement?.paused) {
        this._clonedAudioElement.currentTime = this._audioElement.currentTime;
        this._clonedAudioElement.play().catch(e => logUtishaDebug(`Error playing cloned audio: ${e}`));
      }
    };
    this._onPauseHandler = () => {
      if (!this._clonedAudioElement?.paused) {
        this._clonedAudioElement?.pause();
      }
    };
    this._onSeekedHandler = () => {
      if (this._clonedAudioElement) {
        this._clonedAudioElement.currentTime = this._audioElement.currentTime;
      }
    };

    this._audioElement.addEventListener('play', this._onPlayHandler);
    this._audioElement.addEventListener('pause', this._onPauseHandler);
    this._audioElement.addEventListener('seeked', this._onSeekedHandler);

    // Set the initial volume to match
    this._clonedAudioElement.volume = this._audioElement.volume;
  }

  private removeCloneSyncListeners() {
    if (this._onPlayHandler) {
      this._audioElement.removeEventListener('play', this._onPlayHandler);
      this._onPlayHandler = null;
    }
    if (this._onPauseHandler) {
      this._audioElement.removeEventListener('pause', this._onPauseHandler);
      this._onPauseHandler = null;
    }
    if (this._onSeekedHandler) {
      this._audioElement.removeEventListener('seeked', this._onSeekedHandler);
      this._onSeekedHandler = null;
    }
  }

  /**
   * Gets the display name of this audio source
   */
  get name(): string {
    return this._name;
  }

  /**
   * Gets the unique identifier for this audio source
   */
  get src(): string {
    return this._src;
  }

  set src(src: string) {
    this._src = src;
    this.audioElement.src = src;
    this.audioElement.load();
    if (this._clonedAudioElement) {
      this._clonedAudioElement.src = src;
      this._clonedAudioElement.load();
    }
  }

  /**
   * Plays the audio source
   */
  async play(): Promise<void> {
    try {
      // Don't try to create a source node here to avoid the "already connected" error
      // Let AudioPublisher handle the audio connections
      await this._audioElement.play()
      logUtishaDebug(`Playing audio source: ${this._name} with gain ${this._gain}`);

      const clonedAudioElement = this.clonedAudioElement;
      clonedAudioElement.currentTime = this._audioElement.currentTime;
      await clonedAudioElement.play();
    } catch (error) {
      logUtishaDebug(`Error playing audio source ${this._name}: ${error}`);
    }
  }

  /**
   * Pauses the audio source
   */
  pause(): void {
    try {
      this._audioElement.pause();

      // Also pause cloned element if it exists
      // This is handled by event listeners, but adding for extra safety
      if (this._clonedAudioElement && !this._clonedAudioElement.paused) {
        this._clonedAudioElement.pause();
      }

      logUtishaDebug(`Pausing audio source: ${this._name}`);
    } catch (error) {
      logUtishaDebug(`Error pausing audio source ${this._name}: ${error}`);
    }
  }

  /**
   * Resumes the audio source from the current position
   */
  resume(): void {
    try {
      // Don't try to create a source node here to avoid the "already connected" error
      // Let AudioPublisher handle the audio connections
      this._audioElement.play();
      logUtishaDebug(`Resuming audio source: ${this._name} with gain ${this._gain}`);
    } catch (error) {
      logUtishaDebug(`Error resuming audio source ${this._name}: ${error}`);
    }
  }

  get playing(): boolean {
    return !this._audioElement.paused;
  }

  /**
   * Gets the current gain value (volume level)
   * Range from 0.0 (silent) to 1.0 (full volume)
   */
  get gain(): number {
    return this._gain;
  }

  /**
   * Sets the gain (volume level) of the audio source
   * @param value Gain value from 0.0 (silent) to 1.0 (full volume)
   */
  set gain(value: number) {

    // Apply a much lower volume to the audio element (max 10% of original gain)
    // This helps prevent background music from being too loud
    // Using a very low value (0.1 maximum) to ensure music doesn't overpower voice
    const adjustedVolume = Math.min(0.7, value * 0.5);
    // Clamp value between 0 and 1
    this._gain = Math.max(0, Math.min(1, adjustedVolume));
    this._audioElement.volume = this._gain;
    if (this._clonedAudioElement) {
      this._clonedAudioElement.volume = this._gain;
    }

    // Update gain node if it exists
    if (this._gainNode) {
      this._gainNode.gain.value = this._gain;
      logUtishaDebug(`Updated gain node for ${this._name} to ${this._gain}`);
    }

    logUtishaDebug(`Set gain for ${this._name} to ${value} (actual audio element volume: ${adjustedVolume})`);
  }

  private waitUntilReadyToConnect(): Promise<Awaited<void>[]> {
    const readyCheck = (audioElement: HTMLAudioElement) =>
      new Promise<void>((resolve, reject) => {
        const onCanPlay = () => {
          audioElement.removeEventListener('canplay', onCanPlay);
          resolve();
        };

        const onError = () => {
          audioElement.removeEventListener('error', onError);
          reject(new Error(`Failed to load audio source: ${this.name}`));
        };

        audioElement.addEventListener('canplay', onCanPlay);
        audioElement.addEventListener('error', onError);

        // Trigger loading if not already done
        audioElement.load();
      });

    const readyChecks : Promise<void>[] = [];
    // Ensure the audio element is ready
    if (this.audioElement.readyState < 2) { // HAVE_CURRENT_DATA
      readyChecks.push(readyCheck(this.audioElement));
    }
    if (this.clonedAudioElement.readyState < 2) { // HAVE_CURRENT_DATA
      readyChecks.push(readyCheck(this.clonedAudioElement));
    }
    return Promise.all(readyChecks);
  }
  /**
   * Creates and connects audio nodes for this source
   * @param audioContext The audio context to use
   * @param destination The destination node to connect to
   * @returns The created source node
   */
  async connectToDestination(audioContext: AudioContext, destination: MediaStreamAudioDestinationNode | AudioNode): Promise<MediaElementAudioSourceNode> {
    await this.waitUntilReadyToConnect();
    // If we already have nodes set up with this context, disconnect them first
    if (this._audioContext && this._audioContext !== audioContext) {
      this.disconnectAudioNodes();
    }

    try {
      // Save the context and destination
      this._audioContext = audioContext;
      this._destination = destination;

      // If we already have a source node for this element and context, reuse it
      if (!this._sourceNode || this._sourceNode.context !== audioContext) {
        // Use the cloned audio element to avoid 'already connected' errors
        this._clonedAudioElement = null; // recreate the cloned element
        this._sourceNode = audioContext.createMediaElementSource(this.clonedAudioElement);
        logUtishaDebug(`Created new source node for ${this._name}`);
      }

      // If we already have a gain node for this context, reuse it
      if (!this._gainNode || this._gainNode.context !== audioContext) {
        this._gainNode = audioContext.createGain();
        this._gainNode.gain.value = this._gain;
        logUtishaDebug(`Created new gain node for ${this._name} with gain ${this._gain}`);
      }

      // Connect the nodes
      this._sourceNode.connect(this._gainNode);
      this._gainNode.connect(destination);

      logUtishaDebug(`Connected ${this._name} to destination with gain ${this._gain}`);

      return this._sourceNode;
    } catch (error) {
      logUtishaError(`Error connecting audio nodes for ${this._name}: ${error}`);
      throw error;
    }
  }

  /**
   * Disconnects all audio nodes
   */
  disconnectAudioNodes(): void {
    logUtishaDebug(`Disconnecting audio nodes for ${this._name}`);
    try {
      // Disconnect source node if it exists
      if (this._sourceNode) {
        this._sourceNode.disconnect();
        logUtishaDebug(`Disconnected source node for ${this._name}`);
      }

      // Disconnect gain node if it exists
      if (this._gainNode) {
        this._gainNode.disconnect();
        logUtishaDebug(`Disconnected gain node for ${this._name}`);
      }

      // Clear references
      this._destination = undefined;
    } catch (error) {
      logUtishaError(`Error disconnecting audio nodes for ${this._name}: ${error}`);
    }
  }

  /**
   * Connect this audio source to a specific audio context and destination
   * This allows for seamless addition to existing mixes
   */
  public connectToAudioContext(audioContext: AudioContext, destination: AudioDestinationNode | MediaStreamAudioDestinationNode): void {
    try {
      // Disconnect any existing connections first
      this.disconnectAudioNodes();

      // Create new connection
      const source = audioContext.createMediaElementSource(this._audioElement);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = this._gain;

      // Store references for later cleanup
      this._sourceNode = source;
      this._gainNode = gainNode;

      // Connect: source -> gain -> destination
      source.connect(gainNode);
      gainNode.connect(destination);

      logUtishaDebug(`Connected ${this._name} to audio context`);
    } catch (error) {
      logUtishaError(`Error connecting ${this._name} to audio context: ${error}`);
      throw error;
    }
  }

  /**
   * Gets the current audio context if one exists
   */
  get audioContext(): AudioContext | undefined {
    return this._audioContext;
  }

  /**
   * Gets the current source node if one exists
   */
  get sourceNode(): MediaElementAudioSourceNode | undefined {
    return this._sourceNode;
  }

  /**
   * Gets the current gain node if one exists
   */
  get gainNode(): GainNode | undefined {
    return this._gainNode;
  }
}
